import { useState, useMemo } from 'react';
import { AlertTriangle, ChevronRight, X, Shield, Zap, HelpCircle, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminTabShell } from './AdminTabShell';
import { useToast } from '@/hooks/use-toast';
import {
  type TopologyPlantRow,
  type TopologyClassification,
  useAdminBatteryTopology,
  useSetTopologyOverride,
} from '@/hooks/admin/useAdminBatteryTopology';

// ─── Labels ──────────────────────────────────────────────────────────────

const TOPOLOGY_LABELS: Record<string, string> = {
  shared_bank: 'Shared Bank',
  independent_banks: 'Independent Banks',
  unknown: 'Unknown',
};

const TOPOLOGY_ICONS: Record<string, typeof Shield> = {
  shared_bank: Shield,
  independent_banks: Zap,
  unknown: HelpCircle,
};

const TOPOLOGY_CLASSES: Record<string, string> = {
  shared_bank: 'bg-emerald-900/50 text-emerald-300',
  independent_banks: 'bg-blue-900/50 text-blue-300',
  unknown: 'bg-amber-900/50 text-amber-300',
};

const CONFIDENCE_CLASSES: Record<string, string> = {
  high: 'bg-emerald-900/50 text-emerald-300',
  medium: 'bg-blue-900/50 text-blue-300',
  low: 'bg-amber-900/50 text-amber-300',
};

const SOURCE_LABELS: Record<string, string> = {
  manual_override: 'Manual Override',
  telemetry_correlation: 'Telemetry Correlation',
  fallback: 'Fallback (Insufficient Data)',
};

// ─── Filter types ───────────────────────────────────────────────────────

type TopologyFilter = 'all' | TopologyClassification | 'review_required';

// ─── Detail panel ───────────────────────────────────────────────────────

function TopologyDetailPanel({
  row,
  onClose,
  onOverrideChange,
  isUpdating,
}: {
  row: TopologyPlantRow;
  onClose: () => void;
  onOverrideChange: (override: TopologyClassification | null) => void;
  isUpdating: boolean;
}) {
  const [pendingOverride, setPendingOverride] = useState<TopologyClassification | null | 'cancel'>(null);
  const Icon = TOPOLOGY_ICONS[row.topology] ?? HelpCircle;

  const overrideLabel = (v: TopologyClassification | null) => {
    if (v === null) return 'Auto-detect';
    return TOPOLOGY_LABELS[v] ?? v;
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-wt-bg-primary border-l border-wt-border shadow-xl z-50 overflow-y-auto">
      <div className="p-5 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-wt-text-primary">{row.property_name}</h2>
            <p className="text-xs text-wt-text-muted mt-0.5 font-mono">
              {row.provider_type} &middot; {row.system_count} systems
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Classification badges */}
        <div className="flex flex-wrap gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${TOPOLOGY_CLASSES[row.topology]}`}>
            <Icon className="h-3 w-3" />
            {TOPOLOGY_LABELS[row.topology]}
          </span>
          <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${CONFIDENCE_CLASSES[row.topology_confidence]}`}>
            {row.topology_confidence} confidence
          </span>
          {row.review_required && (
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-amber-900/50 text-amber-300">
              Review Required
            </span>
          )}
          {row.domain_count_approximate && (
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-zinc-700/50 text-zinc-300">
              Approximate Domain Count
            </span>
          )}
        </div>

        {/* Source */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Source</h3>
          <p className="text-wt-text-primary">{SOURCE_LABELS[row.topology_source]}</p>
          {row.topology_source === 'manual_override' && (
            <p className="text-[10px] text-amber-400">
              Manual override takes precedence over telemetry detection.
            </p>
          )}
        </div>

        {/* Effective Capacity */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Effective Capacity</h3>
          <div className="space-y-1.5">
            <div className="flex justify-between">
              <span className="text-wt-text-muted">Configured capacity</span>
              <span className="font-mono text-wt-text-primary">{row.battery_kwh_total.toFixed(1)} kWh</span>
            </div>
            <div className="flex justify-between">
              <span className="text-wt-text-muted">Detected battery domains</span>
              <span className="font-mono text-wt-text-primary">
                {row.battery_domain_count}
                {row.domain_count_approximate && ' (approximate)'}
              </span>
            </div>
            <div className="border-t border-wt-border my-1" />
            <div className="flex justify-between font-medium">
              <span className="text-wt-text-primary">Effective nameplate</span>
              <span className="font-mono text-wt-text-primary">
                {row.effective_nameplate_kwh.toFixed(1)} kWh
                {row.battery_domain_count > 1 && ' per domain'}
              </span>
            </div>
          </div>
        </div>

        {/* Evidence */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Telemetry Evidence</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-wt-text-muted">Overlapping samples</span>
              <span className="ml-2 font-mono text-wt-text-primary">{row.overlapping_buckets}</span>
            </div>
            <div>
              <span className="text-wt-text-muted">Avg SoC delta</span>
              <span className="ml-2 font-mono text-wt-text-primary">
                {row.avg_soc_delta != null ? `${row.avg_soc_delta.toFixed(2)} ppt` : '—'}
              </span>
            </div>
            <div>
              <span className="text-wt-text-muted">p95 SoC delta</span>
              <span className="ml-2 font-mono text-wt-text-primary">
                {row.p95_soc_delta != null ? `${row.p95_soc_delta.toFixed(2)} ppt` : '—'}
              </span>
            </div>
            <div>
              <span className="text-wt-text-muted">Max SoC delta</span>
              <span className="ml-2 font-mono text-wt-text-primary">
                {row.max_soc_delta != null ? `${row.max_soc_delta.toFixed(2)} ppt` : '—'}
              </span>
            </div>
            <div>
              <span className="text-wt-text-muted">Sustained divergence</span>
              <span className="ml-2 font-mono text-wt-text-primary">
                {row.sustained_divergence_count} consecutive
              </span>
            </div>
          </div>
          {row.overlapping_buckets < 20 && (
            <p className="text-[10px] text-amber-400 mt-1">
              Insufficient overlapping telemetry samples (need 20+). Classification may change as more data arrives.
            </p>
          )}
        </div>

        {/* Baseline */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Baseline</h3>
          <div className="flex gap-3">
            <span className="text-wt-text-muted">Status:</span>
            <span className="text-wt-text-primary capitalize">{row.baseline_status ?? '—'}</span>
          </div>
          <div className="flex gap-3">
            <span className="text-wt-text-muted">Confidence:</span>
            <span className="text-wt-text-primary capitalize">{row.baseline_confidence ?? '—'}</span>
          </div>
          {row.baseline_learned_kwh != null && (
            <div className="flex gap-3">
              <span className="text-wt-text-muted">Learned capacity:</span>
              <span className="font-mono text-wt-text-primary">{row.baseline_learned_kwh.toFixed(1)} kWh</span>
            </div>
          )}
        </div>

        {/* Monthly report flag */}
        {row.monthly_report_subscribed && row.topology_confidence === 'low' && (
          <div className="bg-red-900/20 border border-red-800/40 rounded-lg p-3 text-xs">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-red-300 font-medium">Monthly Report Impact</p>
                <p className="text-red-400/80 mt-0.5">
                  This property has an active monthly report subscription but battery topology
                  confidence is low. Capacity figures in reports may be inaccurate until topology
                  is confirmed via telemetry or manual override.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Admin override control */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-3 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Admin Override</h3>
          <p className="text-[10px] text-wt-text-muted leading-relaxed">
            Manual override takes precedence over telemetry correlation. Use when you have
            physical knowledge of the battery installation topology.
          </p>
          <Select
            value={row.battery_topology_override ?? 'auto'}
            onValueChange={(v) => {
              const newOverride = v === 'auto' ? null : (v as TopologyClassification);
              if (newOverride !== row.battery_topology_override) {
                setPendingOverride(newOverride);
              }
            }}
            disabled={isUpdating}
          >
            <SelectTrigger className="h-8 text-xs bg-wt-bg-primary border-wt-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-detect (telemetry)</SelectItem>
              <SelectItem value="shared_bank">Shared Bank</SelectItem>
              <SelectItem value="independent_banks">Independent Banks</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog
        open={pendingOverride !== null && pendingOverride !== 'cancel'}
        onOpenChange={(open) => { if (!open) setPendingOverride(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Battery Topology Override</AlertDialogTitle>
            <AlertDialogDescription>
              You are changing the topology override for <strong>{row.property_name}</strong> from{' '}
              <strong>{overrideLabel(row.battery_topology_override)}</strong> to{' '}
              <strong>{overrideLabel(pendingOverride === 'cancel' ? null : pendingOverride)}</strong>.
              <br /><br />
              This will change how effective battery capacity is calculated for this plant.
              Manual overrides take precedence over automatic telemetry-based detection.
              This change will be logged in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingOverride !== null && pendingOverride !== 'cancel') {
                  onOverrideChange(pendingOverride);
                }
                setPendingOverride(null);
              }}
            >
              Confirm Override
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────

export function BatteryTopologyTab() {
  const { data: rows, isLoading, error } = useAdminBatteryTopology(true);
  const overrideMutation = useSetTopologyOverride();
  const { toast } = useToast();

  const [filter, setFilter] = useState<TopologyFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<TopologyPlantRow | null>(null);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let list = rows;
    if (filter === 'review_required') list = list.filter((r) => r.review_required);
    else if (filter !== 'all') list = list.filter((r) => r.topology === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.property_name.toLowerCase().includes(q));
    }
    return list;
  }, [rows, filter, search]);

  const counts = useMemo(() => {
    if (!rows) return { shared: 0, independent: 0, unknown: 0, review: 0 };
    return {
      shared: rows.filter((r) => r.topology === 'shared_bank').length,
      independent: rows.filter((r) => r.topology === 'independent_banks').length,
      unknown: rows.filter((r) => r.topology === 'unknown').length,
      review: rows.filter((r) => r.review_required).length,
    };
  }, [rows]);

  const handleOverrideChange = (override: TopologyClassification | null) => {
    if (!selectedRow) return;
    const newEffective = override === 'shared_bank'
      ? selectedRow.battery_kwh_total
      : selectedRow.battery_kwh_total / selectedRow.system_count;

    overrideMutation.mutate(
      {
        plantId: selectedRow.plant_id,
        propertyId: selectedRow.property_id,
        override,
        previousKwh: selectedRow.effective_nameplate_kwh,
        effectiveKwh: Math.round(newEffective * 100) / 100,
      },
      {
        onSuccess: () => {
          toast({ title: 'Topology override updated', description: `${selectedRow.property_name}: ${override ?? 'auto-detect'}` });
          setSelectedRow(null);
        },
        onError: (err) => {
          toast({ title: 'Override failed', description: (err as Error).message, variant: 'destructive' });
        },
      },
    );
  };

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      <div className="space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Shared Bank" count={counts.shared} className="text-emerald-400" />
          <SummaryCard label="Independent Banks" count={counts.independent} className="text-blue-400" />
          <SummaryCard label="Unknown" count={counts.unknown} className="text-amber-400" />
          <SummaryCard
            label="Review Required"
            count={counts.review}
            className={counts.review > 0 ? 'text-red-400' : 'text-zinc-500'}
          />
        </div>

        {/* Filters + search */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill label="All" value="all" current={filter} onSelect={setFilter} />
          <FilterPill label="Shared" value="shared_bank" current={filter} onSelect={setFilter} />
          <FilterPill label="Independent" value="independent_banks" current={filter} onSelect={setFilter} />
          <FilterPill label="Unknown" value="unknown" current={filter} onSelect={setFilter} />
          <FilterPill label="Review Required" value="review_required" current={filter} onSelect={setFilter} />
          <div className="ml-auto relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-wt-text-muted" />
            <Input
              placeholder="Search property..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 pl-7 w-48 text-xs bg-wt-bg-secondary border-wt-border"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-wt-text-muted text-left border-b border-wt-border">
                <th className="pb-2 pr-3 font-medium">Property</th>
                <th className="pb-2 pr-3 font-medium">Sys</th>
                <th className="pb-2 pr-3 font-medium">Topology</th>
                <th className="pb-2 pr-3 font-medium">Confidence</th>
                <th className="pb-2 pr-3 font-medium">Source</th>
                <th className="pb-2 pr-3 font-medium text-right">Config kWh</th>
                <th className="pb-2 pr-3 font-medium text-right">Domains</th>
                <th className="pb-2 pr-3 font-medium text-right">Eff. Nameplate</th>
                <th className="pb-2 pr-3 font-medium text-right">Avg SoC Δ</th>
                <th className="pb-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const Icon = TOPOLOGY_ICONS[row.topology] ?? HelpCircle;
                return (
                  <tr
                    key={row.plant_id}
                    className="border-b border-wt-border hover:bg-wt-bg-secondary/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedRow(row)}
                  >
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5">
                        {row.review_required && (
                          <AlertTriangle className="h-3 w-3 text-amber-400 shrink-0" />
                        )}
                        <span className="text-wt-text-primary font-medium truncate max-w-[180px]">
                          {row.property_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 pr-3 font-mono text-wt-text-muted">{row.system_count}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${TOPOLOGY_CLASSES[row.topology]}`}>
                        <Icon className="h-2.5 w-2.5" />
                        {TOPOLOGY_LABELS[row.topology]}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CONFIDENCE_CLASSES[row.topology_confidence]}`}>
                        {row.topology_confidence}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-wt-text-muted">
                      {row.topology_source === 'manual_override' ? 'Override' :
                       row.topology_source === 'telemetry_correlation' ? 'Telemetry' : 'Fallback'}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-wt-text-muted">
                      {row.battery_kwh_total.toFixed(1)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-wt-text-primary">
                      {row.battery_domain_count}
                      {row.domain_count_approximate && <span className="text-amber-400">~</span>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-wt-text-primary">
                      {row.effective_nameplate_kwh.toFixed(1)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-wt-text-muted">
                      {row.avg_soc_delta != null ? row.avg_soc_delta.toFixed(2) : '—'}
                    </td>
                    <td className="py-2">
                      <ChevronRight className="h-3.5 w-3.5 text-wt-text-muted" />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-wt-text-muted">
                    No multi-inverter plants match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selectedRow && (
        <TopologyDetailPanel
          row={selectedRow}
          onClose={() => setSelectedRow(null)}
          onOverrideChange={handleOverrideChange}
          isUpdating={overrideMutation.isPending}
        />
      )}
    </AdminTabShell>
  );
}

// ─── Small components ───────────────────────────────────────────────────

function SummaryCard({ label, count, className }: { label: string; count: number; className?: string }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 border border-wt-border">
      <p className="text-[11px] text-wt-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-mono font-semibold mt-1 ${className ?? 'text-wt-text-primary'}`}>
        {count}
      </p>
    </div>
  );
}

function FilterPill<T extends string>({
  label, value, current, onSelect,
}: { label: string; value: T; current: T; onSelect: (v: T) => void }) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
        active
          ? 'bg-wt-accent/20 text-wt-accent border border-wt-accent/40'
          : 'bg-wt-bg-tertiary text-wt-text-muted border border-transparent hover:border-wt-border'
      }`}
    >
      {label}
    </button>
  );
}
